import { useEffect, useState } from "react";

import Grid from "@mui/material/Grid";

import { useAppDispatch, useAppSelector } from "index";
import DetailItems from "containers/Components/DetailItems";
import useLanguageHook from "containers/hooks/useLanguageHook";
import { OptionKey, VisTypes } from "@store/options/types";
import { useContentSize } from "containers/hooks/useWindowSize";
import { setSidebarFilter } from "@store/filters/actions";
import { FilterType } from "@store/filters/types";
import { selectItem, setCurrentVisualization } from "@store/options/actions";
import { Pagination, Stack, useMediaQuery } from "@mui/material";
import { Box, Theme } from "@mui/system";
import { useDatapointsSelectionFiltered } from "containers/hooks/useData";
import { ResponsiveButton } from "../Components/ResponsiveButton";
import { ArrowDownward as ArrowDownwardIcon, ArrowUpward as ArrowUpwardIcon } from "@mui/icons-material";
import { VisPageWrapper } from "@pages/VisPageWrapper";
const GridImage = new URL("../../../public/grid.png", import.meta.url).href;

function GridListPage() {
  const breakpoint = useMediaQuery((theme: Theme) => theme.breakpoints.up("lg"));

  // Sensitivity
  const { width } = useContentSize();
  const isPrivate = useAppSelector((state) => state.options.options[OptionKey.HideSensitiveData].value);
  const useLinearLayout = useAppSelector((s) => s.options.options[OptionKey.LinearLayout].value);
  const columnCount = useAppSelector((s) => s.options.options[OptionKey.ColumnCount].value);

  const datapoints = useDatapointsSelectionFiltered();

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [direction, setDirection] = useState(false);
  const itemsPerPage = 200;
  const maxPage = Math.ceil(datapoints.length / itemsPerPage);
  const data = getData();
  // Styling
  const { t } = useLanguageHook();
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(setSidebarFilter([FilterType.categories]));
    dispatch(setCurrentVisualization(VisTypes.Grid));
  }, [dispatch]);

  useEffect(() => {
    dispatch(selectItem(OptionKey.LinearLayout, width < 800));
  }, [width, dispatch]);

  function getData() {
    const start = (currentPage - 1) * itemsPerPage;

    if (direction) return datapoints.reverse().slice(start, start + itemsPerPage);
    else return datapoints.slice(start, start + itemsPerPage);
  }

  const renderPagination = () => {
    if (datapoints.length === 0) return undefined;

    return (
      <>
        <Box sx={{ flexGrow: 1 }} />
        <Pagination
          count={maxPage}
          page={currentPage}
          siblingCount={breakpoint ? 1 : 0}
          onChange={(_, val) => setCurrentPage(val)}
        />
        <Box sx={{ flexGrow: 1 }} />
        <ResponsiveButton
          icon={direction ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
          text={direction ? t.Vis.AscendingOrder : t.Vis.DescendingOrder}
          onClick={() => setDirection(!direction)}
          sx={{ align: "right" }}
        />
      </>
    );
  };

  return (
    <VisPageWrapper
      title={t.Vis.TheGrid}
      help={{
        title: t.LandingPage.GridTitle,
        description: t.LandingPage.GridText,
        image: GridImage,
      }}
    >
      <Grid container justifyContent="center" sx={{ py: 3 }}>
        <Grid item xs={12}>
          <Stack direction={"row"} spacing={2} justifyContent="center" pt={1}>
            {renderPagination()}
          </Stack>
          <Grid container alignItems="center" sx={{ m: 0, marginLeft: "1rem", width: "calc(100% - 2rem)" }}>
            {data.map((d, i) => (
              <Grid
                item
                key={d.date.toISOString() + i}
                xs={12 / (useLinearLayout ? 1 : columnCount)}
                alignSelf="stretch"
                sx={{
                  p: 1,
                  width: "100%",
                }}
              >
                <DetailItems data={d} private={isPrivate} index={i} linearLayout={useLinearLayout} />
              </Grid>
            ))}
          </Grid>
          <Stack direction={"row"} spacing={2} justifyContent="center" pt={1}>
            {renderPagination()}
          </Stack>
        </Grid>
      </Grid>
    </VisPageWrapper>
  );
}

export { GridListPage };
